// /mnt/mantenimientos/frontend/src/componentsTecnico/ClienteSelector.jsx
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";

/**
 * ClienteSelector (SaaS)
 * - Pensado para TOPBAR y vistas normales
 * - Controlado: acepta `value` (id) y `isLoading`
 * - Props útiles:
 *   - compact: true para navbar (sin paddings extra, más bajo)
 *   - minHeight: override fino (por defecto 42 / compact 40)
 * - Muestra código si existe (codigo_cliente/codigo/tag_zabbix)
 */

function hasDocument() {
  return typeof document !== "undefined" && !!document?.body;
}

function getCodigoCliente(c) {
  return c?.codigo_cliente || c?.codigo || c?.tag_zabbix || "";
}

const ALL_CLIENTS_VALUE = "__all_clients__";

function toComparableId(v) {
  // permite que "12" y 12 casen
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : String(v);
}

export default function ClienteSelector({
  clientes = [],
  onSeleccionar,
  value = null,
  isLoading = false,

  // NUEVO
  compact = false,
  minHeight, // opcional
  className = "",
}) {
  const [isDark, setIsDark] = useState(() => {
    if (!hasDocument()) return false;
    return document.documentElement.classList.contains("dark");
  });

  useEffect(() => {
    if (!hasDocument()) return;
    const el = document.documentElement;

    const obs = new MutationObserver(() => {
      setIsDark(el.classList.contains("dark"));
    });

    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  const finalMinHeight = useMemo(() => {
    if (Number.isFinite(Number(minHeight))) return Number(minHeight);
    return compact ? 40 : 42;
  }, [minHeight, compact]);

  const lightRSStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        backgroundColor: "var(--panel)",
        borderColor: state.isFocused ? "var(--primary)" : "var(--border)",
        boxShadow: "none",
        minHeight: finalMinHeight,
        borderRadius: 14,
        "&:hover": { borderColor: "var(--primary)" },
      }),
      valueContainer: (base) => ({
        ...base,
        paddingTop: compact ? 0 : base.paddingTop,
        paddingBottom: compact ? 0 : base.paddingBottom,
      }),
      indicatorsContainer: (base) => ({
        ...base,
        height: finalMinHeight,
      }),
      singleValue: (base) => ({ ...base, color: "var(--text)", fontWeight: 800 }),
      placeholder: (base) => ({ ...base, color: "var(--muted)", fontWeight: 700 }),
      menu: (base) => ({
        ...base,
        backgroundColor: "var(--panel)",
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid var(--border)",
        boxShadow: "var(--shadow)",
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "color-mix(in srgb, var(--primary) 18%, var(--panel))"
          : state.isFocused
          ? "color-mix(in srgb, var(--primary) 10%, var(--panel))"
          : "var(--panel)",
        color: "var(--text)",
        cursor: "pointer",
        paddingTop: 10,
        paddingBottom: 10,
      }),
      input: (base) => ({ ...base, color: "var(--text)", fontWeight: 700 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    }),
    [finalMinHeight, compact]
  );

  const darkRSStyles = useMemo(
    () => ({
      ...lightRSStyles,
      control: (base, state) => ({
        ...base,
        backgroundColor: "rgba(15, 23, 42, 0.55)",
        borderColor: state.isFocused ? "var(--primary)" : "var(--border)",
        boxShadow: "none",
        minHeight: finalMinHeight,
        borderRadius: 14,
        "&:hover": { borderColor: "var(--primary)" },
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: "rgba(15, 23, 42, 0.96)",
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid var(--border)",
        boxShadow: "var(--shadow)",
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "rgba(96, 165, 250, 0.18)"
          : state.isFocused
          ? "rgba(96, 165, 250, 0.12)"
          : "rgba(15, 23, 42, 0.96)",
        color: "var(--text)",
        cursor: "pointer",
        paddingTop: 10,
        paddingBottom: 10,
      }),
    }),
    [lightRSStyles, finalMinHeight]
  );

  const options = useMemo(() => {
    return [{ value: ALL_CLIENTS_VALUE, label: "Todos los clientes", isAll: true }, ...(clientes || []).map((c) => {
      const code = getCodigoCliente(c);
      return {
        value: c.id,
        label: c.nombre,
        code,
      };
    })];
  }, [clientes]);

  const selectedOption = useMemo(() => {
    const v = toComparableId(value);
    if (v === null) return options.find((o) => o.isAll) || null;
    return options.find((o) => toComparableId(o.value) === v) || null;
  }, [value, options]);

  return (
    <div
      className={`cliente-select-wrap ${compact ? "compact" : ""} ${className}`}
      style={{
        width: "100%",
        minWidth: 0,
      }}
    >
      <Select
        classNamePrefix="Select"
        menuPortalTarget={hasDocument() ? document.body : undefined}
        styles={isDark ? darkRSStyles : lightRSStyles}
        options={options}
        value={selectedOption}
        isLoading={isLoading}
        placeholder="Todos los clientes"
        isClearable
        onChange={(op) => onSeleccionar?.(op?.isAll ? null : op?.value ?? null)}
        formatOptionLabel={(op) => (
          <div
            className="client-opt"
            style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}
          >
            <div className="client-opt-name" style={{ fontWeight: 800, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {op.label}
            </div>
            {op.code ? (
              <div
                className="client-opt-code"
                style={{
                  fontWeight: 900,
                  fontSize: 12,
                  opacity: 0.85,
                  padding: "4px 8px",
                  borderRadius: 999,
                  border: "1px solid var(--border)",
                  background: "color-mix(in srgb, var(--panel-2) 70%, transparent)",
                  flex: "0 0 auto",
                }}
              >
                {op.code}
              </div>
            ) : null}
          </div>
        )}
      />
    </div>
  );
}
